import React from 'react';
import { StarIcon } from 'lucide-react';
import { Avatar } from './Avatar';
import { avatarColors } from '../data/coaches';
import type { CoachProfile, Testimonial } from '../data/testimonials';
type TestimonialsProps = {
  testimonials: Testimonial[];
  profiles?: CoachProfile[];
  title?: string;
  subtitle?: string;
  background?: 'white' | 'cream';
};

/** Renders nothing until data/testimonials.ts holds real buyer reviews. */
export function Testimonials({
  testimonials,
  profiles = [],
  title = 'What coaches are saying',
  subtitle = 'From volleyball coaches running club, school and rec teams.',
  background = 'white'
}: TestimonialsProps) {
  if (testimonials.length === 0) return null;
  const bg = background === 'white' ? 'bg-white' : 'bg-cloud';
  return <section aria-label={title} className={`w-full text-ink ${bg}`} data-id="element-382" data-mp="v9gbnrq5car9">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-383" data-mp="v8ndvg960vl9">
        <div className="text-center" data-id="element-384" data-mp="qa3r5dmqzfru">
          <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-coral-deep" data-id="element-385" data-mp="qb4zvbgajq6e">Coach to coach</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold text-ink sm:text-5xl" data-id="element-386" data-mp="h8145e8mjyfq">{title}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm text-slate" data-id="element-387" data-mp="m2om0u94hulc">{subtitle}</p>
        </div>

        {profiles.length > 0 && <div className="mt-9 grid gap-6 border-y border-cloud-dark py-7 sm:grid-cols-2 sm:gap-10" data-id="element-388" data-mp="c6bt3ylejkrn">
            {profiles.map((profile, i) => <figure key={profile.name} className="flex gap-4" data-id="element-389" data-mp="zx7thk3xboai" data-mpi={i}>
                <Avatar name={profile.name} color={avatarColors[(i + 1) % avatarColors.length]} className="h-12 w-12 flex-none" data-id="element-390" data-mp="14vslwxcxp3oe" />
                <div data-id="element-391" data-mp="q63ckeuottd1">
                  <figcaption data-id="element-392" data-mp="q4saqkksyvok">
                    <span className="block font-display text-lg font-extrabold leading-tight text-ink" data-id="element-393" data-mp="v57kz39sfn2b">{profile.name}</span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-coral-deep" data-id="element-394" data-mp="1iuifi30jol80">{profile.role}</span>
                  </figcaption>
                  <blockquote className="mt-2 text-sm leading-relaxed text-slate" data-id="element-395" data-mp="1xi0mj81kxrxl">“{profile.bio}”</blockquote>
                </div>
              </figure>)}
          </div>}

        <div className={`mt-9 grid gap-4 ${testimonials.length === 1 ? 'mx-auto max-w-md' : testimonials.length % 2 === 0 && testimonials.length % 3 !== 0 ? 'mx-auto max-w-4xl sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`} data-id="element-396" data-mp="1sh39f9vffo2y">
          {testimonials.map((item, i) => <figure key={item.author} className="flex flex-col rounded-2xl border border-cloud-dark bg-white p-5 shadow-soft" data-id="element-397" data-mp="19r3n2uqz0n38" data-mpi={i}>
              <div className="flex items-center gap-2.5" data-id="element-398" data-mp="10rqdm22ujyii">
                <Avatar name={item.author} src={item.avatar} color={avatarColors[i % avatarColors.length]} className="h-11 w-11" data-id="element-399" data-mp="10tb6zodfdbes" />
                <span className="flex gap-0.5" role="img" aria-label="Five out of five" data-id="element-400" data-mp="vuhu9y5ypofo">
                  {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} className="h-3.5 w-3.5 fill-amber text-amber" aria-hidden="true" data-id="element-401" data-mp="32hp7r7dzqlqc" />)}
                </span>
              </div>
              {item.title && <h3 className="mt-4 font-display text-lg font-extrabold leading-snug tracking-tight text-ink" data-id="element-402" data-mp="3cetqex8mnz2e">{item.title}</h3>}
              <blockquote className="mt-3 space-y-2.5 text-sm leading-relaxed text-slate" data-id="element-403" data-mp="3gekrbtrvbvkn">
                {item.quote.split('\n\n').map(paragraph => <p key={paragraph} data-id="element-404" data-mp="2oqkd6jphcjmv">{paragraph}</p>)}
              </blockquote>
              <figcaption className="mt-auto pt-4 text-xs text-slate" data-id="element-405" data-mp="2squf9la5ipwg">
                <span className="block text-[13px] font-semibold text-ink" data-id="element-406" data-mp="2xhkml42512ts">{item.author}</span>
                {item.role}
              </figcaption>
            </figure>)}
        </div>
      </div>
    </section>;
}